import { z } from 'zod'

import { nullableStringSchema } from './dal-utils'

/**
 * Question asking the user to pick a single value on a numeric scale, with a
 * label anchored at each end (e.g. "Strained/restricted" -> "Free and easy").
 */
const singleChoiceScaleQuestionSchema = z.object({
  kind: z.literal('singleChoiceScale'),
  key: z.string().min(1),
  prompt: z.string().min(1),
  helperText: nullableStringSchema,
  min: z.number().int(),
  max: z.number().int(),
  minLabel: z.string(),
  maxLabel: z.string(),
})

/**
 * Question asking the user to tick zero or more options from a fixed list.
 */
const multipleChoiceQuestionSchema = z.object({
  kind: z.literal('multipleChoice'),
  key: z.string().min(1),
  prompt: z.string().min(1),
  helperText: nullableStringSchema,
  options: z
    .array(
      z.object({
        key: z.string().min(1),
        label: z.string(),
      }),
    )
    .min(1),
})

/**
 * App-facing question item. Discriminated on `kind` so consumers can narrow
 * with `if (question.kind === 'multipleChoice')` and get full type safety.
 */
const questionnaireQuestionSchema = z.discriminatedUnion('kind', [
  singleChoiceScaleQuestionSchema,
  multipleChoiceQuestionSchema,
])

/**
 * Parsed payload stored in `*.data` columns that hold a questionnaire. Kept
 * as a flat list of questions so the same contract backs both the
 * measurement-definition and session-definition `data` columns.
 */
export const questionnaireDataSchema = z.object({
  questions: z.array(questionnaireQuestionSchema),
})

type QuestionnaireData = z.infer<typeof questionnaireDataSchema>

/**
 * Accepts the raw backend representation of a questionnaire `data` field
 * (JSON string, already-parsed object, or `null` / missing) and returns the
 * parsed app-facing contract or `null`. Throws on invalid JSON or on a
 * payload that does not match {@link questionnaireDataSchema}.
 */
export const questionnaireDataInputSchema = z
  .unknown()
  .transform((value, ctx): QuestionnaireData | null => {
    if (value == null) {
      return null
    }

    let parsed: unknown
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value)
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid JSON in questionnaire data',
        })
        return z.NEVER
      }
    } else {
      parsed = value
    }

    const result = questionnaireDataSchema.safeParse(parsed)
    if (!result.success) {
      result.error.issues.forEach((issue) => ctx.addIssue(issue))
      return z.NEVER
    }

    return result.data
  })

/**
 * Serializes a typed questionnaire data object into the JSON string stored
 * in the backend column. Validates against the contract first so the column
 * never receives malformed payloads.
 */
export function serializeQuestionnaireData(data: QuestionnaireData): string {
  return JSON.stringify(questionnaireDataSchema.parse(data))
}
